import type { DexieMigrationTable } from '@infra/persistence/Migrations/dexie_to_sqlite';
import type { SqliteTableName } from '@infra/persistence/SqliteTableName';
import { signalLocalWrite } from '@infra/sync/hybrid/SyncLocalWriteSignal';

export interface SqliteImportResult {
  ok: boolean;
  counts?: Record<string, number>;
  dexiePrimary?: boolean;
  error?: string;
}

export interface SqliteRemoteCacheResult {
  ok: boolean;
  dexiePrimary?: boolean;
  already?: boolean;
  storageProfile?: string;
  error?: string;
}

/** Pont renderer → SQLite chiffré (processus principal Electron). */
/** Jeton attendu par `db:sqliteClearAll` pour un effacement de tous les dossiers. */
export const CLEAR_ALL_TENANTS_CONFIRM = 'EFFACER_TOUS_LES_DOSSIERS';

export class SqliteEntityBridge {
  private static api() {
    return window.electron;
  }

  static isAvailable(): boolean {
    return Boolean(this.api()?.sqliteStatus);
  }

  static async isSqlitePrimary(): Promise<boolean> {
    const status = await this.api()?.sqliteStatus?.();
    return Boolean(status?.available && status.dexiePrimary === false);
  }

  static async importSnapshot(
    snapshots: Array<{ name: DexieMigrationTable; rows: Record<string, unknown>[] }>,
  ): Promise<SqliteImportResult> {
    const fn = this.api()?.sqliteImport;
    if (!fn) return { ok: false, error: 'hors Electron' };
    return fn(snapshots);
  }

  static async ensureRemoteSecureCache(): Promise<SqliteRemoteCacheResult> {
    const fn = this.api()?.sqliteEnsureRemoteCache;
    if (!fn) return { ok: false, dexiePrimary: true, error: 'hors Electron' };
    return fn();
  }

  static async list<T extends { id: string }>(
    tableName: SqliteTableName,
    tenantId?: string,
  ): Promise<T[]> {
    const fn = this.api()?.sqliteList;
    if (!fn) return [];
    return fn(tableName, tenantId) as Promise<T[]>;
  }

  static async get<T extends { id: string }>(
    tableName: SqliteTableName,
    id: string,
  ): Promise<T | null> {
    const fn = this.api()?.sqliteGet;
    if (!fn) return null;
    return fn(tableName, id) as Promise<T | null>;
  }

  static async put<T extends { id: string }>(tableName: SqliteTableName, row: T): Promise<T> {
    const fn = this.api()?.sqlitePut;
    if (!fn) throw new Error('SQLite indisponible');
    const saved = (await fn(tableName, row)) as T;
    signalLocalWrite(tableName);
    return saved;
  }

  static async delete(tableName: SqliteTableName, id: string): Promise<void> {
    const fn = this.api()?.sqliteDelete;
    if (!fn) throw new Error('SQLite indisponible');
    await fn(tableName, id);
    signalLocalWrite(tableName);
  }

  static async count(tableName: SqliteTableName, tenantId?: string): Promise<number> {
    const fn = this.api()?.sqliteCount;
    if (!fn) return 0;
    return fn(tableName, tenantId);
  }

  /** Efface les données d'UNE entreprise (celle de la session). */
  static async clearCurrentTenant(): Promise<void> {
    await this.api()?.sqliteClearAll?.({ scope: 'tenant' });
  }

  /** Efface TOUS les dossiers du poste : confirmation explicite exigée par le processus principal. */
  static async clearAllTenants(): Promise<void> {
    await this.api()?.sqliteClearAll?.({
      scope: 'all-tenants',
      confirm: CLEAR_ALL_TENANTS_CONFIRM,
    });
  }
}
